<template>
  <ul class="speed-bars">
    <li
      v-for="row in rows"
      :key="row.key"
      class="speed-bars__row"
      :class="{'speed-bars__row--clickable': clickable}"
      :title="row.label + ': ' + format(row.value)"
      :tabindex="clickable ? 0 : null"
      @click="clickable && $emit('select', row)"
      @keyup.enter="clickable && $emit('select', row)"
    >
      <span class="speed-bars__label">{{ row.label }}</span>
      <span class="speed-bars__track">
        <span
          class="speed-bars__bar"
          :class="'speed-bars__bar--' + (row.tone || 'normal')"
          :style="{width: width(row.value)}"
        ></span>
      </span>
      <span class="speed-bars__value">{{ format(row.value) }}<small v-if="showShare && total > 0"> {{ share(row.value) }}%</small></span>
    </li>
  </ul>
</template>

<script>
// Ranked or ordered bars with the value at the tip: every number stays readable
// without hovering, and Lao/Thai labels use the page's own font (a canvas would not).
export default {
  name: 'SpeedBars',
  props: {
    // [{key, label, value, tone: 'normal' | 'serious' | 'critical'}]
    rows: {type: Array, default: () => []},
    clickable: {type: Boolean, default: false},
    showShare: {type: Boolean, default: false},
  },
  computed: {
    max() {
      return this.rows.reduce((max, row) => Math.max(max, row.value || 0), 0);
    },
    total() {
      return this.rows.reduce((sum, row) => sum + (row.value || 0), 0);
    },
  },
  methods: {
    width(value) {
      if (!value || !this.max) return '0';
      // a real non-zero value stays visible as a sliver
      return Math.max(1, (value / this.max) * 100) + '%';
    },
    format(value) {
      return new Intl.NumberFormat('en-US').format(value || 0);
    },
    share(value) {
      return (((value || 0) * 100) / this.total).toFixed(1);
    },
  },
};
</script>

<style lang="scss" scoped>
.speed-bars {
  list-style: none;
  margin: 0;
  padding: 0;
}

.speed-bars__row {
  display: grid;
  grid-template-columns: minmax(110px, 38%) 1fr minmax(84px, auto);
  align-items: center;
  column-gap: 10px;
  padding: 5px 4px;
  border-radius: 4px;

  &--clickable {
    cursor: pointer;

    &:hover,
    &:focus {
      background: #f3f2ee;
      outline: none;
    }
  }
}

.speed-bars__label {
  font-size: 0.85rem;
  color: #52514e;
  text-align: left;
}

.speed-bars__track {
  display: block;
  height: 12px;
}

.speed-bars__bar {
  display: block;
  height: 12px;
  border-radius: 0 4px 4px 0;
  background: #2a78d6;

  &--serious {
    background: #ec835a;
  }

  &--critical {
    background: #d03b3b;
  }
}

.speed-bars__value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0b0b0b;
  text-align: right;
  white-space: nowrap;

  small {
    font-weight: 400;
    color: #52514e;
  }
}
</style>
